const STORAGE_KEY_PREFIX = "chat:snapshot:";
const DEFAULT_CHANNEL_KEY = "default";
const TTL_MS = 60 * 60 * 1000;
const MAX_PERSISTED_MESSAGES = 50;
const SNAPSHOT_VERSION = 1;

function getStorage() {
  try {
    if (typeof window === "undefined") {
      return null;
    }

    return window.sessionStorage || null;
  } catch (_) {
    return null;
  }
}

function buildKey(channelId) {
  const safeChannelId =
    typeof channelId === "string" && channelId.length > 0
      ? channelId
      : DEFAULT_CHANNEL_KEY;

  return `${STORAGE_KEY_PREFIX}${safeChannelId}`;
}

function now() {
  return Date.now();
}

function isExpired(updatedAt) {
  if (typeof updatedAt !== "number" || Number.isNaN(updatedAt)) {
    return true;
  }

  return now() - updatedAt > TTL_MS;
}

function isValidSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object") {
    return false;
  }

  if (snapshot.version !== SNAPSHOT_VERSION) {
    return false;
  }

  if (!Array.isArray(snapshot.messages)) {
    return false;
  }

  return true;
}

function truncateMessages(messages) {
  if (!Array.isArray(messages)) {
    return [];
  }

  if (messages.length <= MAX_PERSISTED_MESSAGES) {
    return messages;
  }

  return messages.slice(messages.length - MAX_PERSISTED_MESSAGES);
}

export function load(channelId) {
  const storage = getStorage();

  if (!storage) {
    return null;
  }

  const key = buildKey(channelId);
  const raw = storage.getItem(key);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);

    if (!isValidSnapshot(parsed)) {
      storage.removeItem(key);
      return null;
    }

    if (isExpired(parsed.updatedAt)) {
      storage.removeItem(key);
      return null;
    }

    return {
      conversationId: parsed.conversationId ?? null,
      messages: parsed.messages ?? [],
      messageBuffer: parsed.messageBuffer ?? {},
      updatedAt: parsed.updatedAt,
    };
  } catch (_) {
    storage.removeItem(key);
    return null;
  }
}

export function save(channelId, snapshot) {
  const storage = getStorage();

  if (!storage) {
    return;
  }

  if (!snapshot || typeof snapshot !== "object") {
    return;
  }

  const payload = {
    version: SNAPSHOT_VERSION,
    conversationId: snapshot.conversationId ?? null,
    messages: truncateMessages(snapshot.messages),
    messageBuffer: snapshot.messageBuffer ?? {},
    updatedAt: now(),
  };

  try {
    storage.setItem(buildKey(channelId), JSON.stringify(payload));
  } catch (_) {}
}

export function clear(channelId) {
  const storage = getStorage();

  if (!storage) {
    return;
  }

  try {
    storage.removeItem(buildKey(channelId));
  } catch (_) {}
}

export function hasActiveConversation(channelId) {
  const snapshot = load(channelId);

  return !!(snapshot && snapshot.conversationId);
}

export const __testing = {
  STORAGE_KEY_PREFIX,
  TTL_MS,
  MAX_PERSISTED_MESSAGES,
  SNAPSHOT_VERSION,
  buildKey,
};
